import { useAuth } from '@shared/auth';
import { useTranslation } from '@shared/localization';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { PaymentMethodsPage } from '@/components/pages/payment-methods/PaymentMethodsPage';
import { SessionExpired } from '@/components/pages/SessionExpired';
import { StoredInstrumentsPage } from '@/components/pages/stored-instruments/StoredInstrumentsPage';
import { SepaMandateActivationPage } from '@/components/payment-providers/sepa/SepaMandateActivationPage';
import { SepaMandateConsentPage } from '@/components/payment-providers/sepa/SepaMandateConsentPage';
import { triggerErrorToast } from '@/libs/toasts';
import NavigationService, {
  InternalPage,
  Page,
} from '@/services/navigation/NavigationService';
import { getMandateByReference } from '@/services/payments/instruments/PaymentInstrumentsApi';

import { ErrorHandler } from './components/common/errors/ErrorHandler';
import { PaymentIntentFlow } from './schemas/PaymentIntent';
import PaymentStore from './services/payments/PaymentStore';

const MANDATE_PARAM = 'mandate';

export function ManageApp() {
  const [internalPage, setInternalPage] = useState<Page>();
  const { accessToken } = useAuth();
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const handledMandateDeepLink = useRef(false);

  const pageToRender = useMemo(() => {
    switch (internalPage?.page) {
      case InternalPage.PaymentMethods:
        return <PaymentMethodsPage flow={PaymentIntentFlow.Manage} />;
      case InternalPage.StatusError:
        return <ErrorHandler />;
      case InternalPage.StoredInstruments:
        return (
          <StoredInstrumentsPage
            instruments={PaymentStore.instruments}
            flow={PaymentStore.flow}
            canAddInstruments={PaymentStore.legalEntity !== undefined}
          />
        );
      case InternalPage.SessionExpired:
        return <SessionExpired />;
      case InternalPage.SepaMandateConsent:
        return <SepaMandateConsentPage />;
      case InternalPage.SepaMandateActivation:
        return <SepaMandateActivationPage />;
      default:
        return null;
    }
  }, [internalPage]);

  useEffect(() => {
    const navigationSubscription = NavigationService.subscribe(setInternalPage);
    return () => navigationSubscription.unsubscribe();
  }, []);

  // Entry point for links from the shop to confirm a pending mandate.
  useEffect(() => {
    const mandateReference = searchParams.get(MANDATE_PARAM);
    if (!mandateReference || !accessToken || handledMandateDeepLink.current) {
      return;
    }
    handledMandateDeepLink.current = true;

    getMandateByReference(mandateReference, accessToken)
      .then((details) => {
        // Dropped only after loading so a failed attempt can be retried by refreshing.
        const remainingParams = new URLSearchParams(searchParams);
        remainingParams.delete(MANDATE_PARAM);
        setSearchParams(remainingParams, { replace: true });

        PaymentStore.mandateActivation = { mandateReference, details };
        NavigationService.navigateTo({
          page: InternalPage.SepaMandateActivation,
        });
      })
      .catch(() =>
        triggerErrorToast(
          t('Something went wrong!'),
          t('Failed to load mandate details.'),
        ),
      );
  }, [accessToken, searchParams, setSearchParams, t]);

  return <>{pageToRender}</>;
}
